import { useEffect, useState } from 'react';
import { X, Sparkles, Upload, Trash2, Loader2 } from 'lucide-react';
import api from '../services/apiClient';
import { Button, Textarea } from './ui';
import { toast } from './toast';
import Lightbox from './Lightbox';
import { NETWORK_LABELS, NETWORK_ICONS, NETWORK_COLORS } from '../lib/networks';

/**
 * Modal para gerenciar as imagens de um post (rede): galeria com varias imagens,
 * gerar por IA (com prompt editavel) ou enviar do computador. Cada acao ADICIONA uma imagem.
 * onChange(mediaArray) atualiza o card. media = [{ key, url }].
 */
export default function ImageModal({ targetId, network, currentMedia = [], onChange, onClose }) {
  const [style, setStyle] = useState('design');
  const [prompt, setPrompt] = useState('');
  const [plan, setPlan] = useState(null);
  const [loadingPrompt, setLoadingPrompt] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genCarousel, setGenCarousel] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(null);

  const Icon = NETWORK_ICONS[network];
  const media = currentMedia || [];

  useEffect(() => {
    setLoadingPrompt(true);
    api.get(`/ai/targets/${targetId}/image-prompt`, { style })
      .then((r) => { setPrompt(r.prompt || ''); setPlan(r.plan || null); })
      .catch(() => { setPrompt(''); setPlan(null); })
      .finally(() => setLoadingPrompt(false));
  }, [targetId, style]);

  const generate = async () => {
    if (!prompt.trim()) return toast.error('Escreva uma descrição para a imagem');
    setGenerating(true);
    try {
      const res = await api.post('/ai/generate-image', { targetId, prompt, style });
      onChange(res.media || []);
      toast.success('Imagem adicionada');
    } catch (err) { toast.error(err.message); } finally { setGenerating(false); }
  };

  const generateCarousel = async () => {
    setGenCarousel(true);
    try {
      const res = await api.post('/ai/generate-carousel', { targetId });
      onChange(res.media || []);
      toast.success(`Carrossel gerado (${res.slides} imagens)`);
    } catch (err) { toast.error(err.message); } finally { setGenCarousel(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.upload('/ai/upload-image', file, { targetId });
      onChange(res.media || []);
      toast.success('Imagem adicionada');
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const remove = async (key) => {
    try {
      const res = await api.del(`/ai/targets/${targetId}/image?key=${encodeURIComponent(key)}`);
      onChange(res.media || []);
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 style={{ margin: 0 }} className="row">
            {Icon && <Icon size={18} color={NETWORK_COLORS[network]} />} Imagens — {NETWORK_LABELS[network] || network}
          </h3>
          <button className="icon-btn" onClick={onClose} aria-label="Fechar"><X size={18} /></button>
        </div>

        {media.length > 0 && (
          <div className="modal-section" style={{ marginBottom: 14 }}>
            <strong>{media.length} imagem(ns) no post {media.length > 1 && '(carrossel)'}</strong>
            <div className="media-grid mt8">
              {media.map((m, i) => (
                <div key={m.key} className="media-item">
                  <img src={m.url} alt={`Imagem ${i + 1}`} onClick={() => setZoom({ images: media.map((x) => x.url), index: i })} title="Ver ampliada" />
                  <button type="button" className="gen-thumb-x" onClick={() => remove(m.key)} title="Remover"><X size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {plan?.format === 'carousel' && plan.slides?.length >= 2 && (
          <div className="modal-section" style={{ marginBottom: 14, background: 'var(--surface-2, #f6f7f9)', borderRadius: 8, padding: 12 }}>
            <strong>A IA sugere um carrossel de {plan.slides.length} slides</strong>
            {plan.reason && <p className="muted" style={{ margin: '4px 0 8px' }}>{plan.reason}</p>}
            <Button onClick={generateCarousel} loading={genCarousel} disabled={generating || uploading}>
              <Sparkles size={15} /> Gerar carrossel ({plan.slides.length} imagens)
            </Button>
          </div>
        )}
        {plan?.format === 'single' && (
          <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
            A IA sugere uma imagem única para este post.
          </p>
        )}

        <div className="modal-section">
          <div className="row between">
            <strong>Adicionar por IA</strong>
            {loadingPrompt && <span className="muted row" style={{ fontSize: 12 }}><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> montando prompt…</span>}
          </div>

          <div className="row wrap" style={{ gap: 6, margin: '8px 0' }}>
            <span className={`net-chip ${style === 'design' ? 'on' : ''}`} onClick={() => setStyle('design')}>Arte do post (com texto)</span>
            <span className={`net-chip ${style === 'illustration' ? 'on' : ''}`} onClick={() => setStyle('illustration')}>Ilustração (sem texto)</span>
          </div>

          <p className="muted" style={{ margin: '4px 0 8px' }}>
            {style === 'design'
              ? 'Arte profissional com título, tópicos e a marca. Se a empresa tiver logo (em Empresas), ele é incorporado. Edite o prompt à vontade.'
              : 'Imagem visual sem texto, só a cena. Edite o prompt à vontade.'}
          </p>
          <Textarea rows={6} value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={loadingPrompt}
            placeholder="Descreva a imagem…" />
          <Button onClick={generate} loading={generating} disabled={loadingPrompt} className="mt8">
            <Sparkles size={15} /> Gerar e adicionar
          </Button>
        </div>

        <div className="modal-or"><span>ou</span></div>

        <div className="modal-section">
          <strong>Enviar imagem do computador</strong>
          <label className="upload-drop mt8" style={{ marginTop: 8 }}>
            <input type="file" accept="image/*" hidden disabled={uploading}
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            {uploading
              ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Enviando…</>
              : <><Upload size={18} /> Selecionar arquivo (JPG, PNG, WEBP)</>}
          </label>
        </div>
      </div>

      {zoom && <Lightbox images={zoom.images} index={zoom.index || 0} onClose={() => setZoom(null)} />}
    </div>
  );
}
